import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import api from '@/services/api';

export const useAnnouncementsStore = defineStore('announcements', () => {
    const announcements = ref([]);
    const meta          = ref(null);
    const current = ref(null);
    const loading = ref(false);
    const loadingMore = ref(false);
    // Awaria pobierania listy. Bez tego widok pokazywał „Brak ogłoszeń" także
    // wtedy, gdy serwer odpowiedział błędem — a to dwie różne wiadomości.
    const error   = ref('');
    let pollTimer = null;
    let wczytanychStron = 1;
    // Aktywne odsiewanie listy. Trzymamy je w store, a nie w widoku, bo dotyczy
    // także doładowania kolejnej strony i odpytywania w tle — obie te drogi
    // muszą pytać o to samo, co użytkownik widzi na ekranie.
    const filtry = ref({});

    const unreadCount = computed(() =>
        announcements.value.filter(a => !a.is_read && a.status === 'published').length
    );

    async function fetchList() {
        loading.value = true;
        error.value   = '';
        try {
            const { data } = await api.get('/announcements', { params: filtry.value, skipErrorToast: true });
            // Odpytywanie w tle nie zwija ręcznie doładowanych stron
            if (wczytanychStron === 1) {
                announcements.value = data.data;
                meta.value          = data.meta;
            }
        } catch {
            error.value = 'Nie udało się wczytać ogłoszeń.';
        } finally {
            loading.value = false;
        }
    }

    /**
     * Zmiana odsiewania. Licznik wczytanych stron wraca do jedynki, bo bez tego
     * `fetchList` uznałby listę za ręcznie doładowaną i zostawiłby na ekranie
     * poprzedni wynik — użytkownik wpisałby frazę i nie zobaczył żadnej zmiany.
     */
    async function zastosujFiltry(nowe = {}) {
        filtry.value    = nowe;
        wczytanychStron = 1;

        await fetchList();
    }

    // Doładowanie kolejnej strony — append do listy, bez wymiany
    async function fetchMore() {
        if (!meta.value || meta.value.current_page >= meta.value.last_page) return;
        loadingMore.value = true;
        try {
            const { data } = await api.get('/announcements', {
                params: { ...filtry.value, page: meta.value.current_page + 1 },
            });
            announcements.value = [...announcements.value, ...data.data];
            meta.value          = data.meta;
            wczytanychStron    += 1;
        } finally {
            loadingMore.value = false;
        }
    }

    async function fetchOne(id) {
        loading.value = true;
        try {
            const { data } = await api.get(`/announcements/${id}`);
            current.value = data;
            return data;
        } finally {
            loading.value = false;
        }
    }

    async function create(payload) {
        const { data } = await api.post('/announcements', payload);
        announcements.value.unshift(data);
        if (meta.value) meta.value.total += 1;
        return data;
    }

    async function update(id, payload) {
        const { data } = await api.put(`/announcements/${id}`, payload);
        const idx = announcements.value.findIndex(a => a.id === id);
        if (idx !== -1) announcements.value[idx] = data;
        return data;
    }

    async function publish(id) {
        const { data } = await api.post(`/announcements/${id}/publish`);
        const idx = announcements.value.findIndex(a => a.id === id);
        if (idx !== -1) announcements.value[idx] = data;
        return data;
    }

    async function destroy(id) {
        await api.delete(`/announcements/${id}`);
        const przed = announcements.value.length;
        announcements.value = announcements.value.filter(a => a.id !== id);
        if (meta.value && announcements.value.length < przed) {
            meta.value.total = Math.max(0, meta.value.total - 1);
        }
    }

    async function markRead(id) {
        await api.post(`/announcements/${id}/read`);
        const a = announcements.value.find(a => a.id === id);
        if (a) a.is_read = true;
    }

    // Potwierdzenie zapoznania (ważne ogłoszenia) — osobne od markRead
    async function acknowledge(id) {
        await api.post(`/announcements/${id}/read`, { acknowledge: true });
        const a = announcements.value.find(a => a.id === id);
        if (a) { a.is_read = true; a.is_acknowledged = true; }
    }

    // Nieprzeczytane + ważne-niepotwierdzone — do okna po zalogowaniu
    async function fetchPending() {
        const { data } = await api.get('/announcements/pending');
        return data;
    }

    async function fetchReadStats(id) {
        const { data } = await api.get(`/announcements/${id}/read-stats`);
        return data;
    }

    function startPolling() {
        fetchList();
        pollTimer = setInterval(fetchList, 60_000);
    }

    function stopPolling() {
        if (pollTimer) {
            clearInterval(pollTimer);
            pollTimer = null;
        }
    }

    return {
        announcements, meta, current, loading, loadingMore, error, unreadCount,
        fetchList, fetchMore, fetchOne, zastosujFiltry, create, update, publish, destroy, markRead, acknowledge, fetchPending, fetchReadStats,
        startPolling, stopPolling,
    };
});
